import React from "react";
import { Box, Grid, Paper, Step, StepConnector, StepLabel, Stepper, Typography, stepConnectorClasses } from "@mui/material";
import { styled, useTheme, alpha } from "@mui/material/styles";
import OrderTimeline from "../../OrderTimeLine";
import OrderDetails from "../../OrderDetail";
import Action from "../../Action";
import useIsMobile from "../../isMobile";
import { mapStatusToBucket } from "../trackingTemplateUtils";
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MarkunreadMailboxOutlinedIcon from '@mui/icons-material/MarkunreadMailboxOutlined';

// ── SINGLE SOURCE for the Classic tracking result UI ──────────────────────────────────────────
// The 4-step progress stepper + gradient status card + timeline + order-details grid.
// Rendered by BOTH the live page (Order.js) and the Classic preview template (templates/Classic.js)
// so the preview can never drift from production again. Emits Grid items — the caller provides
// the surrounding <Grid container>.

const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const stepperSteps = [
    { label: 'Packed', shortLabel: 'Packed', icon: 1 },
    { label: 'In Transit', shortLabel: 'Transit', icon: 2 },
    { label: 'Out for Delivery', shortLabel: 'Out', icon: 3 },
    { label: 'Delivered', shortLabel: 'Done', icon: 4 },
];

const stepIcons = {
    1: <InventoryOutlinedIcon sx={{ fontSize: 18 }} />,
    2: <LocalShippingOutlinedIcon sx={{ fontSize: 18 }} />,
    3: <MarkunreadMailboxOutlinedIcon sx={{ fontSize: 18 }} />,
    4: <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />,
};

function getStepperIndex(bucket) {
    const mapping = {
        'Shipment Packed': 0,
        'Shipment In Transit': 1,
        'Out for delivery': 2,
        'Out for Delivery': 2,
        'Delivered': 3,
    };
    return mapping[bucket] ?? -1;
}

export function getStatusColors(textColor, palette) {
    const colorMap = {
        primary: { main: palette.primary.main, light: alpha(palette.primary.main, 0.08), gradient: `linear-gradient(135deg, ${palette.primary.main}, ${palette.primary.light})` },
        success: { main: palette.success.main, light: alpha(palette.success.main, 0.08), gradient: `linear-gradient(135deg, ${palette.success.main}, ${palette.success.light})` },
        error: { main: palette.error.main, light: alpha(palette.error.main, 0.08), gradient: `linear-gradient(135deg, ${palette.error.main}, ${palette.error.light})` },
    };
    return colorMap[textColor] || colorMap.primary;
}

const ColorConnector = styled(StepConnector)(({ theme, statuscolor }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
        top: 16,
    },
    [`&.${stepConnectorClasses.active}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            background: statuscolor || theme.palette.primary.main,
        },
    },
    [`&.${stepConnectorClasses.completed}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            background: statuscolor || theme.palette.primary.main,
        },
    },
    [`& .${stepConnectorClasses.line}`]: {
        height: 2,
        border: 0,
        backgroundColor: '#e0e0e0',
        borderRadius: 1,
    },
}));

function ColorStepIcon({ active, completed, icon, statuscolor }) {
    const theme = useTheme();
    const colors = getStatusColors(statuscolor, theme.palette);
    return (
        <Box
            sx={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                ...(completed || active
                    ? {
                        background: colors.gradient,
                        color: '#fff',
                        boxShadow: `0 2px 6px ${colors.main}30`,
                    }
                    : {
                        bgcolor: '#fafafa',
                        color: '#bdbdbd',
                        border: '1.5px solid #e0e0e0',
                    }),
                transition: 'all 0.3s ease',
            }}
        >
            {stepIcons[icon]}
        </Box>
    );
}

export default function ClassicResult({ data, brand }) {
    const theme = useTheme();
    const isMobile = useIsMobile();

    let textColor = 'primary';
    let edd;
    if (data.createdDate) {
        if (data?.edd) {
            edd = new Date(data?.edd);
        }
        if (data.currentStatus.includes('DELIVERED')) {
            textColor = 'success';
        } else if (data.currentStatus.includes('FAILED')) {
            textColor = 'error';
        }
    }

    const statusBucket = data.createdDate ? mapStatusToBucket(data.currentStatus).replace(/_/g, " ") : '';
    const activeStepIndex = getStepperIndex(statusBucket);
    const showStepper = activeStepIndex >= 0;
    const hasCustomColors = !!brand?.colors;
    const effectiveTextColor = hasCustomColors ? 'primary' : textColor;
    const statusColors = getStatusColors(effectiveTextColor, theme.palette);
    const showEdd = edd && ['Shipment Packed', 'Shipment In Transit', 'Out for delivery', 'Out for Delivery', 'Delivery Failed'].includes(statusBucket);

    if (!data.createdDate) {
        return null;
    }

    return (
        <>
            {showStepper && (
                <Grid item xs={12}>
                    <Paper elevation={0} sx={{ px: { xs: 1.5, sm: 4 }, py: { xs: 1.5, sm: 2 } }}>
                        <Stepper
                            activeStep={activeStepIndex}
                            alternativeLabel
                            connector={<ColorConnector statuscolor={statusColors.main} />}
                        >
                            {stepperSteps.map((step, index) => (
                                <Step key={step.label}>
                                    <StepLabel
                                        StepIconComponent={(props) => (
                                            <ColorStepIcon {...props} statuscolor={effectiveTextColor} icon={step.icon} />
                                        )}
                                    >
                                        <Typography
                                            variant="caption"
                                            fontWeight={index <= activeStepIndex ? 600 : 500}
                                            color={index <= activeStepIndex ? 'text.primary' : 'text.secondary'}
                                            sx={{
                                                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                                lineHeight: 1.2,
                                            }}
                                        >
                                            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>{step.label}</Box>
                                            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>{step.shortLabel}</Box>
                                        </Typography>
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Paper>
                </Grid>
            )}
            <Grid item xs={12} md={6} sx={{ display: 'flex', order: { xs: 2, md: 1 } }}>
                <Paper
                    elevation={0}
                    sx={{
                        width: '100%',
                        overflow: 'hidden',
                    }}>
                    <Box sx={{
                        px: { xs: 2.5, sm: 4 },
                        py: { xs: 2.5, sm: 4 },
                        background: `linear-gradient(135deg, ${statusColors.light} 0%, #ffffff 100%)`,
                        borderBottom: `3px solid ${statusColors.main}`,
                    }}>
                        <Box flexDirection={{
                            xs: 'column',
                            sm: 'row'
                        }}
                            alignItems={{
                                xs: 'flex-start',
                                sm: 'center',
                            }}
                            sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    bgcolor: `${effectiveTextColor}.main`,
                                    boxShadow: `0 0 0 3px ${statusColors.main}30`,
                                }} />
                                <Typography color={`${effectiveTextColor}.main`} variant="h6" fontWeight={700}>
                                    {statusBucket}
                                </Typography>
                            </Box>
                            <Action />
                        </Box>
                        {showEdd && (
                            <Box sx={{ mt: 2, pl: 3.5 }}>
                                <Typography
                                    variant="overline"
                                    fontWeight={600}
                                    component="div"
                                    color="text.secondary"
                                    sx={{ letterSpacing: 1.2, lineHeight: 1.5 }}
                                >
                                    {statusBucket === 'Delivery Failed' ? 'Original EDD' : 'Expected Delivery'}
                                </Typography>
                                <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                                    {days[edd.getDay()]}, {months[edd.getMonth()]} {edd.getDate()}, {edd.getFullYear()}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    <Box sx={{
                        p: { xs: 2.5, sm: 4 },
                        pt: { xs: 2, sm: 3 },
                        maxHeight: { xs: 'none', md: 480 },
                        overflowY: { xs: 'auto', md: 'hidden' },
                        ...(!isMobile ? {
                            '&:hover': {
                                overflowY: 'scroll !important',
                            },
                        } : {}),
                        '&::-webkit-scrollbar': {
                            width: '4px'
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#d0d0d0',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: 'transparent'
                        }
                    }}>
                        <OrderTimeline orderDetailInfo={data} resolvedStatusColor={statusColors.main} />
                    </Box>
                </Paper>
            </Grid>
            <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <OrderDetails data={data} statusBucket={statusBucket} statusColor={effectiveTextColor} />
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
}
